<template>
    <p-form @submit="onSubmit">
        <div class="container-fluid content-wrapper" v-if="storeSetupData">
            <b-row class="content-heading">
                <b-col>
                    <div>Store Setup</div>
                </b-col>
                <b-col cols="auto" class="ms-auto">
                    <p-button type="submit" :is-busy="isBusy" variant="primary">Save</p-button>
                </b-col>
            </b-row>
            <b-tabs justified>
                <b-tab title="Contact Information">
                    <p-card>
                        <b-row cols="1" cols-sm="2" cols-xl="4">
                            <b-col>
                                <p-input
                                    label="Name"
                                    v-model="storeSetupData.locations.name"
                                    rules="max:128|required"
                                />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="Short/Label Name"
                                    v-model="storeSetupData.locations.shortName"
                                    rules="max:24|required"
                                />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="Email"
                                    v-model="storeSetupData.locations.email"
                                    rules="max:254|email"
                                />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="URL"
                                    v-model="storeSetupData.locations.url"
                                    rules="max:255"
                                />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="Abbreviation"
                                    v-model="
                                        storeSetupData.locations.abbreviation
                                    "
                                    rules="max:4|required"
                                />
                            </b-col>
                            <b-col>
                                <p-number
                                    label="Store Number"
                                    input-type="integer"
                                    v-model.number="
                                        storeSetupData.locations.storeNumber
                                    "
                                    rules="required"
                                />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="SAN"
                                    v-model="storeSetupData.locations.san"
                                    rules="max:255"
                                />
                            </b-col>
                            <b-col>
                                <b-form-row>
                                    <b-col cols="7">
                                        <p-phone-number
                                            label="Phone Number"
                                            v-model="
                                                storeSetupData.locations
                                                    .phoneNumber.number
                                            "
                                            rules="max:16"
                                        />
                                    </b-col>
                                    <b-col cols="5">
                                        <p-input
                                            label="Extension"
                                            v-model="
                                                storeSetupData.locations
                                                    .phoneNumber.extension
                                            "
                                            rules="max:6"
                                        />
                                    </b-col>
                                </b-form-row>
                            </b-col>
                        </b-row>
                    </p-card>
                    <p-card>
                        <b-row>
                            <b-col name="shippingAddressSection">
                                <span class="text-center"
                                    ><h5>Shipping</h5></span
                                >
                                <p-address
                                    v-model="
                                        storeSetupData.locations.shippingAddress
                                    "
                                    :fullWidth="true"
                                    require-state-country
                                >
                                </p-address>
                            </b-col>
                            <b-col name="billingAddressSection">
                                <span class="text-center"
                                    ><h5>Billing</h5></span
                                >
                                <p-address
                                    v-model="
                                        storeSetupData.locations.billingAddress
                                    "
                                    :same-as-address="
                                        storeSetupData.locations.shippingAddress
                                    "
                                    :fullWidth="true"
                                    require-state-country
                                />
                            </b-col>
                        </b-row>
                    </p-card>
                </b-tab>
                <b-tab title="Parameters">
                    <p-card title="System Settings">
                        <b-row cols="1" cols-sm="2" cols-xl="4">
                            <b-col>
                                <p-select
                                    label="Main Location"
                                    v-model="storeSetupData.parameters.singleLocationId"
                                    @change="onMainLocationChanged"
                                    rules="required"
                                    data-type="locations"
                                />
                            </b-col>
                            <b-col>
                                <p-select
                                    label="Time Zone"
                                    v-model="storeSetupData.parameters.timeZone"
                                    :options="
                                        storeSetupData.parameters.timeZones
                                    "
                                    rules="required"
                                />
                            </b-col>
                        </b-row>
                        <b-row cols="1" cols-sm="2" cols-xl="4">
                            <b-col>
                                <p-input
                                    label="Organization ID (Optional; used for Apple DEP)"
                                    v-model="
                                        storeSetupData.parameters.depOrganizationId
                                    "
                                    rules="max:2000"
                                />
                            </b-col>
                        </b-row>
                    </p-card>
                    <p-card title="Web Orders">
                        <div class="web-orders-draggable">
                            <p-draggable
                                v-model="storeSetupData.webOrderLocations"
                                label="Web Order Fulfillment Locations"
                                additional-options-label="Available Locations"
                                :additional-options="availableLocations"
                            ></p-draggable>
                        </div>
                        <b-row cols="1" cols-xl="3" class="mt-3">
                            <b-col>
                                <p-checkbox
                                    :use-switch="true"
                                    v-model="storeSetupData.parameters.prismWebUseStockOnHandForAllLocations"
                                    label="Share Stock on Hand for All Locations"
                                    size="md">
                                </p-checkbox>
                                <h5 class="text-muted">
                                    When off, only the main location's stock on hand is shared with PrismWeb.
                                </h5>
                            </b-col>
                        </b-row>
                    </p-card>
                    <p-card title="Book Pricing Settings">
                        <b-row cols="1" cols-sm="2" cols-xl="4">
                            <b-col>
                                <p-select
                                    label="Currency Rounding (Up)"
                                    v-model="
                                        storeSetupData.parameters
                                            .currencyRounding
                                    "
                                    :options="options"
                                    rules="required"
                                    text-field="value"
                                />
                            </b-col>
                        </b-row>
                        <b-row cols="1" cols-sm="2" cols-xl="4">
                            <b-col>
                                <p-number
                                    label="New Price - Net Price Margin"
                                    input-type="percent"
                                    v-model="
                                        storeSetupData.parameters
                                            .textbooksNewPriceNetPriceMargin
                                    "
                                    rules="required|p-decimal:8,2|max_value:99.99"
                                />
                            </b-col>
                            <b-col>
                                <p-number
                                    label="Used Retail Price - Percent of New Price"
                                    input-type="percent"
                                    v-model="
                                        storeSetupData.parameters
                                            .textbooksUsedRetailPricePercentOfNewPrice
                                    "
                                    rules="required|p-decimal:8,2"
                                />
                            </b-col>
                            <b-col>
                                <p-number
                                    label="Used Cost - Percent of New Price"
                                    input-type="percent"
                                    v-model="
                                        storeSetupData.parameters
                                            .textbooksUsedCostPercentOfNewPrice
                                    "
                                    rules="required|p-decimal:8,2"
                                />
                            </b-col>
                            <b-col>
                                <p-select
                                    label="Update Used Prices from New"
                                    :options="yesNoOptions"
                                    v-model="
                                        storeSetupData.parameters
                                            .textbooksUpdateUsedPricesFromNew
                                    "
                                    mode="lazy"
                                    rules="required"
                                />
                            </b-col>
                        </b-row>
                    </p-card>
                    <p-card title="Course Materials Settings">
                        <b-row cols="1" cols-sm="2" cols-xl="3">
                            <b-col>
                                <p-select
                                    label="Shelf Tag Template"
                                    v-model="
                                        storeSetupData.parameters
                                            .shelfTagTemplateOption
                                    "
                                    :options="shelfTagTemplateOptions"
                                    text-field="value"
                                />
                            </b-col>
                            <b-col>
                                 <p-select
                                        label="Rental Agreement"
                                        data-type="rentalAgreement"
                                        v-model="rentalAgreementId"
                                        :disabled="storeSetupData.parameters.rentalAgreementId != null && storeSetupData.parameters.disableRentalAgreement"
                                    />
                                    <div v-if="noRentalAgreement" style="color:red;">
                                        Rental Agreement must be set in Thunder Admin.
                                    </div>
                                    <div v-if="storeSetupData.parameters.rentalAgreementId != null && storeSetupData.parameters.disableRentalAgreement"  style="color:red;">
                                        Cannot change rental agreement while active rentals exist.
                                    </div>
                             </b-col>
                             <b-col>
                                <p-select dataType="taxItems"
                                v-model="
                                        storeSetupData.parameters
                                            .rentalTaxItemId
                                    "
                                    label="Rental Tax Type" />
                              </b-col>
                        </b-row>
                    </p-card>
                    <p-card title="Book Default DCC Settings">
                        <b-row cols="1" cols-xl="3">
                            <b-col>
                                <p-advanced-select
                                    label="New Book DCC"
                                    data-type="dcc"
                                    v-model="
                                        storeSetupData.parameters
                                            .textbooksDefaultNewDCCId
                                    "
                                    :filter="2"
                                />
                            </b-col>
                            <b-col>
                                <p-advanced-select
                                    label="Used Book DCC"
                                    data-type="dcc"
                                    v-model="
                                        storeSetupData.parameters
                                            .textbooksDefaultUsedDCCId
                                    "
                                    :filter="2"
                                />
                            </b-col>
                            <b-col>
                                <p-advanced-select
                                    label="Trade Book DCC"
                                    data-type="dcc"
                                    v-model="
                                        storeSetupData.parameters
                                            .textbooksDefaultTradeDCCId
                                    "
                                    add-empty-option
                                />
                            </b-col>
                        </b-row>
                    </p-card>
                    <p-card>
                        <b-row>
                            <b-col cols="auto">
                                <h4>Digital Content</h4>
                            </b-col>
                            <b-col cols="auto" class="ms-auto">
                                <p-checkbox
                                        :use-switch="true"
                                        v-model="storeSetupData.parameters.enableDigitalContent"
                                        class="text-end"
                                        label="Enable"
                                        size="lg"
                                >
                                </p-checkbox>
                            </b-col>
                        </b-row>
                        <div v-if="storeSetupData.parameters.enableDigitalContent">
                        <b-row cols="1" cols-xl="3">
                             <b-col>
                                <p-advanced-select
                                    label="Digital Book DCC"
                                    data-type="dcc"
                                    v-model="
                                        storeSetupData.parameters
                                            .textbooksDefaultDigitalDCCId
                                    "
                                />
                            </b-col>
                            <b-col>
                                <p-advanced-select
                                    label="Digital Receipt Info"
                                    data-type="digitalAgreements"
                                    v-model="
                                        storeSetupData.parameters
                                            .digitalAgreementId
                                    "
                                />
                            </b-col>
                        </b-row>
                        <b-row cols="1" cols-xl="3" class="mt-3">
                             <b-col>
                                <p-checkbox label="Auto-Adopt Digital Titles"
                                            v-model="storeSetupData.parameters
                                            .autoAdoptDigitalTitles">
                                </p-checkbox>
                            </b-col>
                        </b-row>
                        </div>
                    </p-card>
                    <p-card title="Point Of Sale" class="mt-3">
                        <b-row cols="1" cols-sm="2" cols-xl="6">
                            <b-col>
                                <p-timepicker
                                    label="Store Open"
                                    rules="required"
                                    v-model="
                                        storeSetupData.parameters.storeOpen
                                    "
                                />
                            </b-col>
                            <b-col>
                                <p-timepicker
                                    label="Store Close"
                                    rules="required"
                                    v-model="
                                        storeSetupData.parameters.storeClose
                                    "
                                />
                            </b-col>
                        </b-row>
                    </p-card>
                    <p-card title="Tax Rate Names">
                        <b-row>
                            <b-col cols="4" md="3" lg="2">
                                <h5>Tax Rate Name</h5>
                            </b-col>
                            <b-col cols="8" md="9" lg="10">
                                <h5>Custom Tax Name</h5>
                            </b-col>
                        </b-row>
                        <template v-for="(item, index) in taxRates" :key="index + 'row'">
                            <b-row>
                                <b-col cols="4" md="3" lg="2" class="pe-5">
                                    Tax Rate {{ index + 1 }}
                                </b-col>
                                <b-col cols="8" md="9" lg="10" class="pt-1">
                                    <p-input
                                        :name="`TaxName${index + 1}`"
                                        rules="required|max:80"
                                        v-model="
                                            storeSetupData.parameters[item]
                                        "                                        
                                    />
                                </b-col>
                            </b-row>
                        </template>
                    </p-card>
                    <p-card title="Digital Materials" name="digitalMaterialsCard">
                        <b-row cols="1" cols-sm="2" cols-xl="4">
                            <b-col>
                                <p-input
                                    label="Username"
                                    v-model="storeSetupData.parameters.digitalUsername"
                                    rules="max:400"
                                />
                            </b-col>
                            <b-col>
                                <p-input
                                    type="password"
                                    label="Password"
                                    v-model="storeSetupData.parameters.digitalPassword"
                                    rules="max:400"
                                />
                            </b-col>
                            <b-col>
                                <p-input
                                    label="Site ID"
                                    v-model="storeSetupData.parameters.digitalSiteId"
                                    rules="max:400"
                                />
                            </b-col>
                        </b-row>
                    </p-card>
                </b-tab>
                <p-feature-flag ref="StoreSetupAccountingFlag" name="Store Setup - Accounting">   
                    <b-tab title="Accounting">
                        <p-feature-flag ref="AccountingFlag" name="Accounting">
                            <p-card title="Global Settings">
                                <b-row cols="1" cols-sm="2" cols-xl="4">
                                    <b-col>
                                        <p-select
                                            label="Fiscal Year start month"
                                            v-model="
                                                storeSetupData.parameters
                                                    .fiscalYearStartMonth
                                            "
                                            :disabled="!selectedLocationMatchesDefaultLocation || !isAuthorized"
                                            rules="required"
                                            data-type="monthname"
                                        />
                                    </b-col>
                                </b-row>
                            </p-card>
                        </p-feature-flag>
                        <p-card title="Location Settings">
                            <b-row cols="1" cols-sm="2" cols-xl="6">
                            <p-feature-flag ref="AccountingFlag" name="Accounting">
                                    <b-col>
                                        <p-checkbox
                                            :use-switch="true"
                                            v-model="storeSetupData.parameters.accounting.useGLManagement"                                       
                                            label="Use GL Management"
                                            :disabled="!isAuthorized"
                                            size="md"
                                            @change="useGLManagement">
                                        </p-checkbox>
                                    </b-col>
                                </p-feature-flag>
                                    <b-col>
                                        <p-checkbox
                                            :use-switch="true"
                                            v-model="storeSetupData.parameters.accounting.salesReconciliationAutoPost"                                       
                                            label="Auto Post Sales Reconciliation"
                                            :disabled="!isAuthorized"
                                            size="md">
                                        </p-checkbox>                                      
                                    </b-col>
                            </b-row>
                            <b-row cols="1" cols-sm="2" cols-xl="3" class="mt-2">
                                <b-col>
                                    <p-timepicker
                                        label="End of Accounting Day(00:00 - 23:59)"
                                        v-model="
                                            storeSetupData.parameters.accounting.endOfAccountingDay
                                        "
                                        :disabled="!isAuthorized"
                                    />
                                    <h5 class="text-muted">
                                        Changing the End of Accounting Day applies to future business days only.
                                        Existing Sales Reconciliation records keep the boundaries they were created with.
                                    </h5>
                                </b-col>
                            </b-row>
                        </p-card>						
                        <p-feature-flag ref="AccountingFlag" name="Accounting">
                            <p-card title="Sales Tax Ledger Codes">
                                <TaxGLCodeLocation
                                    :items="this.taxCodeLocations"
                                    :fields="taxGLCodeLocationFields"
                                    @change="markDirty" />
                            </p-card>                        
                            <p-card title="Location GL Codes">
                                <b-row>
                                    <b-col>
                                        <GlCodeStoreSetupLocation
                                            :items="this.storeSetupData.storeSetupLocationChartOfAccountsTypes"
                                            :fields="glCodeStoreSetupLocationFields"
                                            @change="markDirty" />
                                    </b-col>
                                </b-row>
                            </p-card>
                        </p-feature-flag>
                    </b-tab>
                </p-feature-flag>
            </b-tabs>
            <b-row class="mt-4">
                <b-col class="mb-3 text-center">
                    <p-button
                        variant="primary"
                        @click="cancelEdit"
                        class="me-2"
                        :is-busy="isBusy"
                        >Cancel</p-button
                    >
                    <p-button variant="primary" :is-busy="isBusy" type="submit">Save</p-button>
                </b-col>
            </b-row>
        </div>
    </p-form>
</template>

<script>
import axios from 'axios';
import NavigationGuard from '@/components/mixins/NavigationGuard.js';
import StoreListManager from '@/components/mixins/StoreListManager.js';
import EditPage from '@/components/mixins/EditPage';
import selectListOptionsDataContext from '@/services/selectListOptions.dataContext.js';
import GlCodeStoreSetupLocation from '@/components/GLCodeStoreSetupLocation';
import TaxGLCodeLocation from '@/components/TaxGLCodeLocation';
import { useAppStateStore } from '@/stores/appState.js';

export default {
    mixins: [NavigationGuard, StoreListManager, EditPage],
    components: {
        GlCodeStoreSetupLocation,
        TaxGLCodeLocation
    },      
    props: {
        id: String
    },
    data() {
        return {
            storeSetupData: null,
            isBusy: false,
            oldCurrencyRounding: null,
            options: [
                { value: '0.01' },
                { value: '0.05' },
                { value: '0.10' },
                { value: '0.25' },
                { value: '0.50' },
                { value: '1.00' }
            ],
            yesNoOptions: [
                { value: 'true', text: 'Yes' },
                { value: 'false', text: 'No' }
            ],
            selectListOptions: {
                taxRateNames: [],
                locations: []
            },
            shelfTagTemplateOptions: [
                { value: 'Portrait-1x3 (3 shelf tags)' },
                { value: 'Portrait-2x3 (6 shelf tags)' },
                { value: 'Landscape-2x2 (4 shelf tags)' }
            ],
            glCodeStoreSetupLocationFields:[
                {
                    key: 'chartOfAccountsTypeDescription',
                    label: '',
                },
                {
                    key: 'glCode',
                    label: 'GL Code',
				}
            ],
            rentalAgreementId:null,
            availableLocations: [],
            singleLocationId: null,
            taxGLCodeLocationFields:[
                {
                    key: 'locationName',
                    label: 'Location',
                },
                {
                    key: 'posGlCode',
                    label: 'POS GL Code',
                },
                {
                    key: 'webGlCode',
                    label: 'Web Order GL Code',
                }
            ]            
        };
    },
    watch: {
        storeSetupData: NavigationGuard.$watcher
    },
    computed: {
        taxRates() {
            return this.selectListOptions.taxRateNames?.map(
                x => x.value[0].toLowerCase() + x.value.slice(1)
            );
        },
        noRentalAgreement() {
            return this.rentalAgreements.length <= 0;
        },
        selectedLocationMatchesDefaultLocation() {
            const appStateStore = useAppStateStore();
            return appStateStore.getSingleLocationId == appStateStore.getSelectedLocationId;
        },
        isAuthorized() {
            return this.$currentUserHasRole('Admin');
        }
    },
    methods: {
        async loadData() {
            let locationId = await this.getSelectedLocationId();

            this.rentalAgreements = await selectListOptionsDataContext.getStoreDropdownData('rentalAgreement');
            axios.get('storesetup', {
                params: { locationId: locationId }
            }).then(resp => {
                this.storeSetupData = resp.data;
                this.storeSetupData.webOrderLocations = this.storeSetupData.webOrderLocations ?? [];

                this.rentalAgreementId = this.storeSetupData.parameters.rentalAgreementId;
                this.oldCurrencyRounding = this.storeSetupData.parameters.currencyRounding;
                this.taxCodeLocations =  resp.data.taxCodeLocations;

                this.requireWebOrderFulfillmentMainLocation();
                this.singleLocationId = this.storeSetupData.parameters.singleLocationId;

                this.availableLocations = this.selectListOptions.locations
                    .filter(x => !this.storeSetupData.webOrderLocations.some(y => y.locationId === x.value))
                    .map(x => ({ name: x.text, locationId: x.value }));

                this.dataLoaded();
            });
        },
        onSubmit() {
            if (
                this.oldCurrencyRounding &&
                this.storeSetupData.parameters.currencyRounding !==
                    this.oldCurrencyRounding && this.storeSetupData.parameters.currencyRounding != '0.01'
            ) {
                this.$bvModal
                    .msgBoxConfirm(
                        'Do you want to update all retail prices and margins to reflect the new rounding method?',
                        {
                            okTitle: 'YES',
                            cancelTitle: 'NO',
                            noCloseOnBackdrop: true
                        }
                    )
                    .then(value => {
                        this.storeSetupData.parameters.isUpdateAllRetailPricesAndMargins = value;
                        this.postStoreSetup();
                    });
            } else {
                this.postStoreSetup();
            }
        },
        postStoreSetup() {
            this.storeSetupData.parameters.rentalAgreementId = this.rentalAgreementId;
            this.isBusy = true;
            axios.post('storesetup', this.storeSetupData).then(() => {
                this.dataSaved();
                this.oldCurrencyRounding = this.storeSetupData.parameters.currencyRounding;
                const fulfillmentLocationIds = this.storeSetupData.webOrderLocations.map(x => x.locationId);
                useAppStateStore().updateWebOrderFulfillmentLocationIds(fulfillmentLocationIds);
                this.$appSuccess('Store setup saved successfully.')
            })
            .finally(()=>{
                this.isBusy = false;
            });
        },
        cancelEdit() {
            this.$router.push('/home');
        },
        // GL code editors mutate item objects inside child components (the tax
        // one is bound to a non-reactive instance property), so the deep
        // storeSetupData watcher doesn't reliably catch them. Flip the dirty
        // flag explicitly when a GL code changes so the nav guard fires.
        markDirty() {
            this.formIsDirty = true;
        },
        onMainLocationChanged(newLocationId) {
            const previousLocationId = this.singleLocationId;
            this.singleLocationId = newLocationId;
            if (previousLocationId && newLocationId && previousLocationId !== newLocationId) {
                // Unlock old main location but keep it in Web Order Fulfillment Locations
                const previousWebOrderLocationIndex = this.storeSetupData.webOrderLocations.findIndex(x => x.locationId === previousLocationId);
                if (previousWebOrderLocationIndex >= 0) {
                    this.storeSetupData.webOrderLocations.splice(previousWebOrderLocationIndex, 1, {
                        ...this.storeSetupData.webOrderLocations[previousWebOrderLocationIndex],
                        locked: false
                    });
                }

                // Find the new main location name from whichever list it's in
                let locationName = null;
                const newAvailableLocationIndex = this.availableLocations.findIndex(x => x.locationId === newLocationId);
                if (newAvailableLocationIndex >= 0) {
                    locationName = this.availableLocations[newAvailableLocationIndex].name;
                    this.availableLocations.splice(newAvailableLocationIndex, 1);
                }

                const newWebOrderLocationIndex = this.storeSetupData.webOrderLocations.findIndex(x => x.locationId === newLocationId);
                if (newWebOrderLocationIndex >= 0) {
                    if (!locationName) locationName = this.storeSetupData.webOrderLocations[newWebOrderLocationIndex].name;
                    this.storeSetupData.webOrderLocations.splice(newWebOrderLocationIndex, 1);
                }

                if (!locationName) {
                    const newSelectListLocation = this.selectListOptions.locations.find(x => x.value === newLocationId);
                    if (newSelectListLocation) {
                        locationName = newSelectListLocation.text;
                    }
                }

                if (locationName) {
                    this.storeSetupData.webOrderLocations.unshift({ name: locationName, locationId: newLocationId, locked: true });
                }
            }
        },
        requireWebOrderFulfillmentMainLocation() {
            const singleLocationId = this.storeSetupData.parameters.singleLocationId;
            if (singleLocationId) {
                const existingIndex = this.storeSetupData.webOrderLocations.findIndex(x => x.locationId === singleLocationId);
                if (existingIndex >= 0) {
                    // The main location should always be first in the list
                    const singleLocationIndex = this.storeSetupData.webOrderLocations.splice(existingIndex, 1)[0];
                    this.storeSetupData.webOrderLocations.unshift({ ...singleLocationIndex, locked: true });
                }
                else {
                    const mainLocation = this.selectListOptions.locations.find(x => x.value === singleLocationId);
                    if (mainLocation) {
                        this.storeSetupData.webOrderLocations.unshift({
                            name: mainLocation.text,
                            locationId: mainLocation.value,
                            locked: true
                        });
                    }
                }
            }
        },
        useGLManagement() {
            this.$bvModal
                    .msgBoxConfirm(
                        'Are you sure you want to change this setting? GL Management requires that GL Codes be linked to data throughout Mosaic. When this toggle is on users will be prompted to enter or confirm GL numbers. If the prompt is turned off, previously entered values will persist, but new transactions may be saved with incomplete information',
                        {
                            okTitle: 'Ok',
                            cancelTitle: 'Cancel'
                        }
                    )
                    .then(value => {
                        if(!value){
                            this.storeSetupData.parameters.accounting.useGLManagement = !this.storeSetupData.parameters.accounting.useGLManagement;
                        }
                    });
        }
    },
    mounted: function() {
        this.loadData();
    }
};
</script>
<style scoped lang="scss">
.web-orders-draggable {
    width: 50%;
}

.web-orders-draggable :deep(.ps-2 ) {
    font-weight: bold;
    padding-bottom: 0.5rem !important;
}

.web-orders-draggable :deep(.draggable-component),
.web-orders-draggable :deep(.draggable-options-component) {
    max-height: 300px;
    min-height: 200px;
    box-shadow: 0 0 0 1px #ccc;
}

.web-orders-draggable :deep(.list-item ) {
    border: none !important;
    box-shadow: inset 0 -1px 0 #ddd !important;
}
</style>
